import type { ProductFeatureArea, ProductFeatureSurface } from "./product-feature-definition";

/**
 * The internal "Feature usage" dashboard (PREC-4336, usage analytics 9/10): the response shapes of
 * the staff-only read endpoints under `GET /product-analytics/usage/…`, shared so the web page can
 * read them without a copy.
 *
 * Privacy limits (PREC-4337, binding): no response holds a user id, a user code or a per-user
 * figure. Distinct users exist per company, feature and UTC day only; a period shows their mean
 * over zero-filled company-days ("Avg daily distinct users"), never a 7/30/90-day distinct count,
 * never WAU/MAU. Every figure covers complete UTC days through the last nightly rollup.
 */

/** The periods the per-company tabs offer, in complete UTC days. */
export const PRODUCT_USAGE_PERIODS = [7, 30, 90] as const;

export type ProductUsagePeriod = (typeof PRODUCT_USAGE_PERIODS)[number];

/** The All customers tab also offers the last 12 months (it reads the retained totals). */
export const PRODUCT_USAGE_ALL_CUSTOMERS_PERIODS = [7, 30, 90, 365] as const;

export type ProductUsageAllCustomersPeriod = (typeof PRODUCT_USAGE_ALL_CUSTOMERS_PERIODS)[number];

/**
 * Signalist's own workspace and the internal test company: left out of every cohort unless the
 * "include company 1 and 2225" toggle is on (answer 9). Staff are always out: their events are
 * dropped when they are written, so no toggle can bring them back.
 */
export const PRODUCT_USAGE_INTERNAL_COMPANY_IDS = [1, 2225] as const;

/** "Near-zero" on the Unused tab: at most this share of the cohort's companies used it (answer 4). */
export const PRODUCT_USAGE_NEAR_ZERO_ADOPTION_SHARE = 0.01;

/** The filters every per-company tab echoes back. */
export interface ProductUsageFilters {
	readonly period: ProductUsagePeriod;
	/** A `product.id` (the plan); null = all plans. */
	readonly planId: number | null;
	readonly includeTrialDemo: boolean;
	readonly includeInternal: boolean;
}

/** The complete UTC days a figure covers; dates are `YYYY-MM-DD`. */
export interface ProductUsageWindow {
	readonly from: string;
	readonly to: string;
	readonly days: number;
	/** The preceding window of equal length, for the trend. */
	readonly previousFrom: string;
	readonly previousTo: string;
}

/** Every response says how fresh it is and which complete days it covers. */
export interface ProductUsageFreshness {
	/**
	 * The newest day the nightly rollup (or a staff re-run) completed; null before the first one.
	 * A company purge's totals do not move it.
	 */
	readonly lastRolledUpDate: string | null;
	/** Null when nothing is rolled up yet (the empty state). */
	readonly window: ProductUsageWindow | null;
}

/** One plan held by companies that met every other cohort rule, for the Plan filter. */
export interface ProductUsagePlanOption {
	readonly planId: number;
	readonly name: string;
	readonly companies: number;
}

/**
 * Which companies a figure counts (answers 2, 3, 9). Paying = a live subscription that has
 * started; trial = a live subscription that starts in the future; demo = no live subscription and
 * a running demo. Every member also has analytics switched on and at least one event in the window.
 */
export interface ProductUsageCohort {
	readonly kind: "paying" | "paying-trial-demo";
	readonly size: number;
	/** One sentence naming the cohort, for the tab heading. */
	readonly description: string;
	readonly excludedCompanyIds: readonly number[];
	readonly staffExcluded: true;
	readonly plans: readonly ProductUsagePlanOption[];
}

/** Events per surface, and each surface's share of the feature's events (0 to 1). */
export interface ProductUsageSurfaceSplit {
	readonly events: Readonly<Record<ProductFeatureSurface, number>>;
	readonly shares: Readonly<Record<ProductFeatureSurface, number>>;
}

/**
 * Event change against the preceding equal window, same cohort (answer 5): `percent` is
 * (current − previous) / previous × 100; "new" when previous is 0 and current is not; "none" when
 * both are 0.
 */
export type ProductUsageTrend =
	| { readonly kind: "change"; readonly percent: number }
	| { readonly kind: "new" }
	| { readonly kind: "none" };

/** A catalog entry as every tab names it. */
export interface ProductUsageFeatureRef {
	readonly key: string;
	readonly label: string;
	readonly area: ProductFeatureArea;
	readonly featureCode: string | null;
}

export interface ProductUsageAdoptionRow extends ProductUsageFeatureRef {
	readonly companiesUsed: number;
	/** companiesUsed / cohort size, 0 to 1. */
	readonly adoption: number;
	readonly events: number;
	readonly previousEvents: number;
	readonly trend: ProductUsageTrend;
	/**
	 * Mean of the per-company, per-day deduplicated distinct users over cohort × covered days,
	 * zero days included; null when no day of the window is covered by the distinct-user rollup.
	 */
	readonly avgDailyDistinctUsers: number | null;
	readonly surfaces: ProductUsageSurfaceSplit;
}

export interface ProductUsageAreaGroup<Row> {
	readonly area: ProductFeatureArea;
	readonly features: readonly Row[];
}

/**
 * The days of the window the nightly rollup (or a staff re-run) completed, a day without events
 * included. Only these days were measured: a distinct-user mean divides by their count, and any
 * other day of the window is "not measured", never zero. They need not be contiguous.
 */
export interface ProductUsageDistinctUsersCoverage {
	/** The covered days of the window, oldest first (`YYYY-MM-DD`). */
	readonly distinctUsersDates: readonly string[];
	/** How many there are: the days a distinct-user mean is taken over. */
	readonly distinctUsersDays: number;
}

/** `GET /product-analytics/usage/adoption`. */
export interface ProductUsageAdoptionResponse
	extends ProductUsageFreshness,
		ProductUsageDistinctUsersCoverage {
	readonly filters: ProductUsageFilters;
	readonly cohort: ProductUsageCohort;
	readonly catalogSize: number;
	readonly areas: readonly ProductUsageAreaGroup<ProductUsageAdoptionRow>[];
}

export type ProductUsageUnusedReason = "zero" | "near-zero" | "flag-on-never-used";

export interface ProductUsageCompanyRef {
	readonly companyId: number;
	readonly name: string;
}

export interface ProductUsageUnusedRow extends ProductUsageFeatureRef {
	readonly reasons: readonly ProductUsageUnusedReason[];
	readonly companiesUsed: number;
	readonly adoption: number;
	readonly events: number;
	/** Cohort companies whose flag resolves on today; null when the entry has no flag link. */
	readonly flagOnCompanies: number | null;
	/** Flag-on cohort companies with no retained usage of the feature (answer 10). */
	readonly flagOnNeverUsedCompanies: number;
	/** Up to {@link PRODUCT_USAGE_NEVER_USED_SAMPLE} of them, by name. */
	readonly flagOnNeverUsedSample: readonly ProductUsageCompanyRef[];
	/** Latest day with events among cohort companies across retained history; null = no history. */
	readonly lastEventDate: string | null;
}

export const PRODUCT_USAGE_NEVER_USED_SAMPLE = 10;

/** `GET /product-analytics/usage/unused`. */
export interface ProductUsageUnusedResponse extends ProductUsageFreshness {
	readonly filters: ProductUsageFilters;
	readonly cohort: ProductUsageCohort;
	readonly nearZeroAdoptionShare: number;
	readonly areas: readonly ProductUsageAreaGroup<ProductUsageUnusedRow>[];
}

/** One company with retained usage, for the Company view picker. */
export interface ProductUsageCompanyOption extends ProductUsageCompanyRef {
	readonly lastActivityDate: string;
}

/** `GET /product-analytics/usage/companies`. */
export interface ProductUsageCompaniesResponse {
	readonly lastRolledUpDate: string | null;
	readonly companies: readonly ProductUsageCompanyOption[];
}

export interface ProductUsageCompanyFeatureRow extends ProductUsageFeatureRef {
	readonly events: number;
	/** Events per day of `days`, all actions and surfaces; 0 = none recorded. */
	readonly dailyEvents: readonly number[];
	/** Deduplicated distinct users per day of `days`; null for a day the rollup did not cover. */
	readonly dailyDistinctUsers: readonly (number | null)[];
	readonly avgDailyDistinctUsers: number | null;
	readonly lastEventDate: string | null;
}

/** `GET /product-analytics/usage/companies/:companyId`. */
export interface ProductUsageCompanyResponse
	extends ProductUsageFreshness,
		ProductUsageDistinctUsersCoverage {
	readonly period: ProductUsagePeriod;
	readonly company: ProductUsageCompanyRef & { readonly analyticsEnabled: boolean };
	/** Catalog features with at least one event in the window. */
	readonly featuresUsed: number;
	readonly catalogSize: number;
	/** Latest day with any event across retained history (daily precision). */
	readonly lastActivityDate: string | null;
	/** The days of the window, oldest first; the daily arrays align with it. */
	readonly days: readonly string[];
	/** Features with at least one event in the window, grouped by catalog area. */
	readonly areas: readonly ProductUsageAreaGroup<ProductUsageCompanyFeatureRow>[];
}

export interface ProductUsageAllCustomersRow extends ProductUsageFeatureRef {
	readonly events: number;
	readonly surfaceEvents: Readonly<Record<ProductFeatureSurface, number>>;
	/** Events per day of `days`. */
	readonly dailyEvents: readonly number[];
}

/**
 * `GET /product-analytics/usage/all-customers` (answer 11): reads the retained all-customers
 * totals only, deleted accounts included, so no company, plan or trial filter applies. Events
 * only: the stored distinct users and accounts there are sums across companies and slices.
 */
export interface ProductUsageAllCustomersResponse extends ProductUsageFreshness {
	readonly period: ProductUsageAllCustomersPeriod;
	readonly days: readonly string[];
	readonly areas: readonly ProductUsageAreaGroup<ProductUsageAllCustomersRow>[];
}
